import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/shared/lib/api'

// ── Dashboard ──────────────────────────────────────
export const useLLRDashboard = () =>
  useQuery({
    queryKey: ['llr', 'dashboard'],
    queryFn: () => api.get('/llr/dashboard').then((r) => r.data.data),
  })

// ── Radars ─────────────────────────────────────────
export const useRadars = () =>
  useQuery({
    queryKey: ['llr', 'radars'],
    queryFn: () => api.get('/llr/radars').then((r) => r.data.data),
  })

export const useRadar = (id: string) =>
  useQuery({
    queryKey: ['llr', 'radars', id],
    queryFn: () => api.get(`/llr/radars/${id}`).then((r) => r.data.data),
    enabled: !!id,
  })

export const useCreateRadar = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: any) => api.post('/llr/radars', data).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['llr'] }),
  })
}

export const useUpdateRadar = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: any) => api.patch(`/llr/radars/${id}`, data).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['llr'] }),
  })
}

export const useDeleteRadar = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/llr/radars/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['llr'] }),
  })
}

// ── Alerts ─────────────────────────────────────────
export const useAlerts = (params?: { status?: string; radar_id?: string; page?: number }) =>
  useQuery({
    queryKey: ['llr', 'alerts', params],
    queryFn: () => api.get('/llr/alerts', { params }).then((r) => r.data),
  })

export const useAlert = (id: string) =>
  useQuery({
    queryKey: ['llr', 'alerts', id],
    queryFn: () => api.get(`/llr/alerts/${id}`).then((r) => r.data.data),
    enabled: !!id,
  })

export const useAcknowledgeAlert = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/llr/alerts/${id}/acknowledge`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['llr'] }),
  })
}

// ── Actions ────────────────────────────────────────
export const useGenerateActions = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (alertId: string) =>
      api.post(`/llr/actions/generate?alert_id=${alertId}`).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['llr', 'alerts'] }),
  })
}

// ── Trends ─────────────────────────────────────────
export const useTrendHistory = (radarId: string, keyword?: string) =>
  useQuery({
    queryKey: ['llr', 'trends', radarId, keyword],
    queryFn: () =>
      api
        .get('/llr/trends/history', { params: { radar_id: radarId, keyword } })
        .then((r) => r.data.data),
    enabled: !!radarId,
  })

// ── Places ─────────────────────────────────────────
export const useNearbyPlaces = (radarId: string) =>
  useQuery({
    queryKey: ['llr', 'places', radarId],
    queryFn: () => api.get(`/llr/places/nearby?radar_id=${radarId}`).then((r) => r.data.data),
    enabled: !!radarId,
  })
